import React, { useMemo } from "react";

import { Card, GridLegacy, Alert } from "@mui/material";

import CardBody from "../../../../components/Card/CardBody";

import { convertObjectToSelectOptions } from "../../../../templateHelpers";
import FormTextTC from "../../../../components/Forms/FormTextTC";
import FormSelectTC from "../../../../components/Forms/FormSelectTC";
import FormAsyncTC from "../../../../components/Forms/FormAsyncTC";
import { useController, useFormContext } from "react-hook-form";
import FormJson from "../../../../components/Forms/FormJson";
import FormSwitchTC from "../../../../components/Forms/FormSwitchTC";
import TranslationWrapper, { getTranslation } from "../../../../components/TranslationWrapper/TranslationWrapper";
import MenuSyncSettings from "../MenuSyncSettings";
import AsyncProductAutocompleteTC from "../../../../components/Forms/AsyncAutocomplete/Product/AsyncProductAutocompleteTC";
import AuthenticationWrapper from "../../../../auth/AuthenticationWrapper";
import FormDatetimeTC from "../../../../components/Forms/FormDatetimeTC";

const retrieveCategoryOptions = convertObjectToSelectOptions([
  { value: 1, label: "Family Groups" },
  { value: 2, label: "Screen look ups (SLU)" },
]);
const syncLanguageOptions = convertObjectToSelectOptions([
  { value: "en-US", label: "en-US" },
  { value: "nl-NL", label: "nl-NL" },
  { value: "nl-BE", label: "nl-BE" },
  { value: "en-GB", label: "en-GB" },
  { value: "fr-FR", label: "fr-FR" },
]);
const orderMode = [
  { value: 1, label: "Table" },
  { value: 2, label: "Delivery" },
];
const stsVersion = [
  { value: "soap", label: "SOAP | Transaction service GEN 1, deprecated" },
  { value: "soapex2", label: "SOAP | Transaction service GEN 2, limited functionality" },
  { value: "rest", label: "REST | Transaction Service GEN 2" },
];

function Simphony() {
  const {
    field: { value: serviceType, onChange: serviceTypeOnChange },
  } = useController({ name: "apiaccounts__oracleS2ServiceType" });

  const {
    field: { value: customData },
  } = useController({ name: "custom_data" });

  const definitionSequenceOptions = useMemo(() => {
    const definitionSequenceArray = customData?.oracleRest2?.definition_sequences ?? [];

    // Default value to be added at the start of the array
    const defaultValue = {
      value: "ALL",
      label: getTranslation("custom_data.oracleRest2.definition_sequences.all", "ALL"),
    };
    const legacyDefaultValue = {
      value: false,
      label: getTranslation("custom_data.oracleRest2.definition_sequences", "NONE"),
    };

    const convertedArray = [
      defaultValue,
      legacyDefaultValue,
      ...definitionSequenceArray.map((value) => ({
        value: value,
        label: value,
      })),
    ];

    return convertObjectToSelectOptions(convertedArray);
  }, [customData]);

  const tagListOptions = useMemo(() => {
    const definitionSequenceArray = customData?.oracleRest2?.tag_list ?? [];

    const convertedArray = definitionSequenceArray.map((value) => ({
      value: value,
      label: value,
    }));

    return convertObjectToSelectOptions(convertedArray);
  }, [customData]);

  const orderModeOptions = useMemo(() => convertObjectToSelectOptions(orderMode), []);

  const stsVersionOptions = useMemo(() => convertObjectToSelectOptions(stsVersion), []);
  const { menukaartVestigingId, vestigingId } = useFormContext();
  return (
    <div style={{ height: "100%", width: "100%", overflow: "hidden" }}>
      <Card>
        <CardBody alignItems={"flex-start"} style={{ padding: 16, display: "flex", flexWrap: "wrap" }}>
          <GridLegacy container spacing={1} style={{ maxWidth: 1400 }}>
            <FormSelectTC
              name={"apiaccounts__oracleS2ServiceType"}
              options={stsVersionOptions}
              tDefault={"Transaction service version"}
              tKey={"vestigingen.form.micros.label.oracleS2ServiceType"}
            />
            {serviceType == "rest" ? (
              <>
                <FormTextTC name={"apiaccounts__oracleS2RestEndpoint"} tKey={"Simphony cloud endpoint"} />
                <FormTextTC name={"apiaccounts__oracleS2RestAuthServer"} tKey={"Simphony cloud auth server"} />
                <FormTextTC name={"apiaccounts__oracleS2RestUsername"} tKey={"Simphony cloud username"} />
                <FormTextTC name={"apiaccounts__oracleS2RestPassword"} tKey={"Simphony cloud password"} />
                <FormTextTC name={"apiaccounts__oracleS2RestClientID"} tKey={"Simphony cloud clientid"} />
                <FormTextTC
                  name={"apiaccounts__oracleS2RestCompany"}
                  tDefault={"Company name"}
                  tKey={"apiaccounts__oracleS2RestCompany"}
                />
                <FormAsyncTC
                  async={{ url: `core/oracle/getLocations?vid=${vestigingId}`, searchField: "naam" }}
                  multiple={false}
                  name={"apiaccounts__oracleS2RestLocRef"}
                  tDefault={"Simphony cloud location reference (locRef)"}
                  tKey={"apiaccounts__oracleS2RestLocRef"}
                />
                <FormAsyncTC
                  async={{ url: `core/oracle/getRevenueCenters?vid=${vestigingId}`, searchField: "naam" }}
                  multiple={false}
                  name={"apiaccounts__oracleS2RevenueCenter"}
                  tDefault={"Revenue center"}
                  tKey={"apiaccounts__oracleS2RevenueCenter"}
                />
                <FormAsyncTC
                  async={{ url: `core/oracle/getMenus?vid=${vestigingId}`, searchField: "naam" }}
                  multiple={false}
                  name={"apiaccounts__oracleS2RestMenuId"}
                  tDefault={"Sync following menus"}
                  tKey={"apiaccounts__oracleS2RestMenuId"}
                />

                <FormAsyncTC
                  async={{ url: `core/oracle/getTenders?vid=${vestigingId}`, searchField: "naam" }}
                  multiple={false}
                  name={"apiaccounts__oracleS2Tender"}
                  tDefault={"Closing tender (online)"}
                  tKey={"apiaccounts__oracleS2Tender"}
                />
                <FormAsyncTC
                  async={{ url: `core/oracle/getTenders?vid=${vestigingId}`, searchField: "naam" }}
                  multiple={false}
                  name={"apiaccounts__oracleS2TenderCash"}
                  tDefault={"Closing tender (cash)"}
                  tKey={"apiaccounts__oracleS2TenderCash"}
                />
                <FormAsyncTC
                  async={{ url: `core/oracle/getOrderTypes?vid=${vestigingId}`, searchField: "naam" }}
                  multiple={false}
                  name={"apiaccounts__oracleS2OrderType"}
                  tDefault={"Order type"}
                  tKey={"apiaccounts__oracleS2OrderType"}
                />
                <FormSelectTC
                  name={"apiaccounts__oracleS2OrderMode"}
                  options={orderModeOptions}
                  tDefault={"Order mode"}
                  tKey={"vestigingen.form.micros.label.orderMode"}
                />
                <FormSelectTC
                  defaultValue={"en-US"}
                  name={"custom_data.oracleRest2.sync_default_language"}
                  options={syncLanguageOptions}
                  tDefault={"Sync language"}
                  tKey={"custom_data.oracleRest2.sync_default_language"}
                />

                {/*<FormAsyncTC*/}
                {/*  name={"apiaccounts__oracleS2UserID"}*/}
                {/*  tKey={"apiaccounts__oracleS2UserID"}*/}
                {/*  tDefault={"User ID"}*/}
                {/*  async={{ url: `core/oracle/getEmployees?vid=${vestigingId}`, searchField: "naam" }}*/}
                {/*  multiple={false}*/}
                {/*/>*/}
                <FormTextTC name={"apiaccounts__oracleS2UserID"} tKey={"User ID"} />

                <MenuSyncSettings v2Only={true} />

                <FormSwitchTC
                  name={"useAccountServer"}
                  tDefault={"Use price keys/sequences"}
                  tKey={"vestigingen.form.oracle.label.usePriceKeys"}
                />
                <FormSwitchTC
                  name={"useAccountServer_input"}
                  tDefault={"Show discount button for use of price keys"}
                  tKey={"vestigingen.form.oracle.label.show_discount_button"}
                />
                <FormSwitchTC
                  name={"apiaccounts__useCheckCalculator"}
                  tDefault={"Use check calculator"}
                  tKey={"vestigingen.form.oracle.label.useCheckCalculator"}
                />
                <FormSwitchTC
                  name={"oChecker"}
                  tDefault={"Order checker"}
                  tKey={"vestigingen.form.overige.label.orderChecker"}
                />
                <AsyncProductAutocompleteTC
                  async_url={`core/producten/search?fields=id as value,naam as label,id,naam,snaam,blocked,is_actief,prijs&per_page=50&sort_field=naam&vestiging_id=${menukaartVestigingId}`}
                  key={`apiaccounts__checkCalculatorProductId${menukaartVestigingId}`}
                  name={"apiaccounts__checkCalculatorProductId"}
                  nullable={true}
                  tDefault={"Check calculation article"}
                  tKey={"vestigingen.form.apiaccounts.label.checkCalculatorProductId"}
                />

                <Alert severity={"warning"} style={{ width: "100%", marginRight: "30px" }}>
                  <TranslationWrapper
                    tDefault={
                      "WARNING: Changing definition from or to ALL will cause all existing data modified in Jamezz to be lost!"
                    }
                    tKey={"product.form.pause_inds21adsafo"}
                  />
                </Alert>
                <FormSelectTC
                  defaultValue={false}
                  name={"custom_data.oracleRest2.definition_sequence_value"}
                  options={definitionSequenceOptions}
                  tDefault={"Definition Sequence"}
                  tKey={"custom_data.oracleRest2.definition_sequence_value"}
                />
                <FormSelectTC
                  defaultValue={1}
                  name={"custom_data.oracleRest2.category_type"}
                  options={retrieveCategoryOptions}
                  tDefault={"Retrieve category by"}
                  tKey={"custom_data.oracleRest2.category_type"}
                />

                <FormSwitchTC
                  defaultValue={false}
                  name={"custom_data.oracleRest2.tags_filter"}
                  tKey={"Tags enable filter"}
                />
                <FormSelectTC
                  defaultValue={[]}
                  multiple={true}
                  name={"custom_data.oracleRest2.tags_whitelist"}
                  options={tagListOptions}
                  tKey={"Tags allow list"}
                />
                <FormSelectTC
                  defaultValue={[]}
                  multiple={true}
                  name={"custom_data.oracleRest2.tags_blacklist"}
                  options={tagListOptions}
                  tKey={"Tags deny list"}
                />

                <FormJson
                  name={"extra_settings__paymentApiIds"}
                  tDefault={"JSON Payment API Ids"}
                  tKey={"vestigingen.form.paymentApiIds.headers.json"}
                />
                <GridLegacy container spacing={1} style={{ maxWidth: 1400 }}>
                  <GridLegacy item xs={12}>
                    <h3>
                      <TranslationWrapper tDefault={"JSON"} tKey={"vestigingen.form.customfields.headers.json"} />
                    </h3>
                  </GridLegacy>

                  <FormJson
                    name={"extra_settings__account_to_pricekey"}
                    tDefault={"JSON"}
                    tKey={"vestigingen.form.customfields.headers.json"}
                  />
                </GridLegacy>
              </>
            ) : (
              <>
                <FormTextTC name={"apiaccounts__oracleS2Endpoint"} tKey={"Oracle Simphony endpoint"} />
                <FormTextTC name={"apiaccounts__oracleS2Tender"} tKey={"Closing tender (online)"} />
                <FormTextTC name={"apiaccounts__oracleS2TenderCash"} tKey={"Closing tender (cash)"} />

                <FormTextTC name={"apiaccounts__oracleS2OrderType"} tKey={"Order type"} />
                <FormSelectTC
                  name={"apiaccounts__oracleS2OrderMode"}
                  options={orderModeOptions}
                  tDefault={"Order mode"}
                  tKey={"vestigingen.form.micros.label.orderMode"}
                />
                <FormTextTC name={"apiaccounts__oracleS2RevenueCenter"} tKey={"Revenue center"} />
                <FormTextTC name={"apiaccounts__oracleS2UserID"} tKey={"User ID"} />
                <FormAsyncTC
                  async={{
                    url:
                      "core/producten/search?fields=id as value, naam as label&per_page=50&sort_field=naam&vestiging_id=" +
                      menukaartVestigingId,
                    searchField: "naam",
                  }}
                  key={"apiaccounts__oracleS2Bill" + menukaartVestigingId}
                  name={"apiaccounts__oracleS2Bill"}
                  nullable={true}
                  tDefault={"Bediening (service)"}
                  tKey={"vestigingen.form.apiaccounts.label.serviceProduct_id"}
                />
                <FormAsyncTC
                  async={{
                    url:
                      "core/producten/search?fields=id as value, naam as label&per_page=50&sort_field=naam&vestiging_id=" +
                      menukaartVestigingId,
                    searchField: "naam",
                  }}
                  key={"apiaccounts__oracleS2Service" + menukaartVestigingId}
                  name={"apiaccounts__oracleS2Service"}
                  nullable={true}
                  tDefault={"Rekening (service)"}
                  tKey={"vestigingen.form.apiaccounts.label.billProduct_id"}
                />

                <AuthenticationWrapper
                  child={
                    <div>
                      <h4>
                        <TranslationWrapper
                          tDefault={"Automatically restore products blocked by the order checker."}
                          tKey={"orderchecker.restore_blocked.description"}
                        />
                      </h4>
                      <Alert severity={"info"}>
                        <TranslationWrapper
                          tDefault={
                            "If both time settings are filled in, the system will use whichever time comes first to unblock the products."
                          }
                          tKey={"orderchecker.restore_blocked.example"}
                        />
                      </Alert>
                    </div>
                  }
                  permissionKey={"custom_data.orderchecker.restore_blocked_delay"}
                />

                <FormDatetimeTC
                  // initialValue={"1970-01-01T00:00:00"}
                  dateFormat={""}
                  name={"custom_data.orderchecker.restore_blocked_delay"}
                  tDefault={"Block duration. (Unblock automatically after a certain amount of time)"}
                  tKey={"orderchecker.restore_blocked.delay_label"}
                  timeFormat={"HH:mm"}
                />
                <FormDatetimeTC
                  // initialValue={"1970-01-01T00:00:00"}
                  dateFormat={""}
                  name={"custom_data.orderchecker.restore_blocked_time"}
                  tDefault={"Daily unblock time. (Unblock automatically at a daily fixed time)"}
                  tKey={"orderchecker.restore_blocked.specific_time_label"}
                  timeFormat={"HH:mm"}
                />
              </>
            )}

            <h1>Configuration export service</h1>
            <FormTextTC name={"apiaccounts__oracleS2CIEUrl"} tKey={"Endpoint"} />
            <FormTextTC name={"apiaccounts__oracleS2CIECompany"} tKey={"Company"} />
            <FormTextTC name={"apiaccounts__oracleS2CIEUser"} tKey={"Username"} />
            <FormTextTC name={"apiaccounts__oracleS2CIEPass"} tKey={"Password"} />
            <FormTextTC name={"apiaccounts__oracleS2CHierStrucId"} tKey={"Hierarchy id"} />
            <FormTextTC name={"apiaccounts__oracleS2CLanguageId"} tKey={"Language id"} />
          </GridLegacy>
        </CardBody>
      </Card>
    </div>
  );
}

export default Simphony;
